import StyledSwitch from '@/components/common/StyledSwitch'
import Layout from '@/components/layout/Layout'
import CustomButton from '@/components/Returns/CustomButton'
import CustomSelectBase from '@/components/Returns/CustomSelect'
import { useSidebar } from '@/contexts/SidebarContext'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import {
  createRule,
  fetchRuleConditions,
  fetchServiceMasterValues,
  fetchTransactionalDataMetadata,
  updateRule,
  type RuleCondition,
  type RuleCreateRequest,
  type RuleUpdateRequest,
  type TransactionalField,
} from '@/multi-territory/mt-services/mt-rule-setting-api'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/en-gb'
import { useCallback, useEffect, useState } from 'react'

const CustomSelect = CustomSelectBase as any

export const OPERATOR_OPTIONS = [
  { value: 'EQUAL_TO', label: 'Equal To' },
  { value: 'NOT_EQUAL_TO', label: 'Not Equal To' },
  { value: 'LIKE', label: 'Contains' },
  { value: 'IS_NULL', label: 'Is Null' },
  { value: 'IS_NOT_NULL', label: 'Is Not Null' },
  { value: 'IN_LIST', label: 'In List (Excel word)' },
  { value: 'NOT_IN_LIST', label: 'NOT In List  (Excel word)' },
  { value: 'STARTS_WITH', label: 'Starts With' },
  { value: 'ENDS_WITH', label: 'Ends With' },
  {
    value: 'IN_LIST_PROBABLE_MATCH',
    label: 'In List (Contains Probable Match)',
  },
  {
    value: 'NOT_IN_LIST_PROBABLE_MATCH',
    label: 'NOT In List (Contains Probable Match)',
  },
  { value: 'GREATER_THAN', label: 'Greater Than' },
  { value: 'LESS_THAN', label: 'Less Than' },
  { value: 'BETWEEN', label: 'Between' },
  { value: 'GREATER_THAN_OR_EQUAL_TO', label: 'Greater Than Or Equal To' },
  { value: 'LESS_THAN_OR_EQUAL_TO', label: 'Less Than Or Equal To' },
]
const LOGICAL_OPERATOR_OPTIONS = [
  { value: 'AND', label: 'AND' },
  { value: 'OR', label: 'OR' },
]

// Operators that only make sense for numeric / date fields. For other field
// types these are hidden from the Operator dropdown (parity with the India
// AddRuleSetting form).
const NUMERIC_ONLY_OPERATORS = [
  'BETWEEN',
  'GREATER_THAN',
  'LESS_THAN',
  'GREATER_THAN_OR_EQUAL_TO',
  'LESS_THAN_OR_EQUAL_TO',
]
const OPERATORS_WITHOUT_NUMERIC = OPERATOR_OPTIONS.filter(
  op => !NUMERIC_ONLY_OPERATORS.includes(op.value)
)
// Operators whose value is a comma-separated list (free-text even for dates).
const LIST_OPERATORS = [
  'IN_LIST',
  'NOT_IN_LIST',
  'IN_LIST_PROBABLE_MATCH',
  'NOT_IN_LIST_PROBABLE_MATCH',
]
const DATE_DDMMYYYY_REGEX = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/
// Normalise a metadata field type to a coarse data type used for operator/value
// behaviour. Anything not explicitly numeric or date is treated as string.
const mtFieldDataType = (type?: string): 'number' | 'date' | 'string' => {
  const t = (type || '').toLowerCase()
  if (t === 'number' || t === 'numeric' || t === 'decimal' || t === 'integer')
    return 'number'
  if (t === 'date' || t === 'datetime') return 'date'
  return 'string'
}

interface FormCondition {
  id: number
  conditionId?: string
  field: string
  operator: string
  value: string
  logicalOperator: 'AND' | 'OR'
}

export interface RuleSettingRow {
  uuid: string
  ruleName: string
  mappedNatureOfService: string
  startDate: string
  endDate: string
  status: string
}

interface MTRuleSettingFormProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  editingRule: RuleSettingRow | null
  countryCode: string
  apiIdentifierUuid: string
  tanApplicable: boolean
}

export function MTRuleSettingForm({
  open,
  onClose,
  onSuccess,
  editingRule,
  countryCode,
  apiIdentifierUuid,
  tanApplicable,
}: MTRuleSettingFormProps) {
  const isEditMode = !!editingRule
  const { showSnackbar } = useSnackbar()
  const { isCollapsed } = useSidebar()
  const [ruleName, setRuleName] = useState('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [active, setActive] = useState(true)
  const [conditions, setConditions] = useState<FormCondition[]>([
    { id: 1, field: '', operator: '', value: '', logicalOperator: 'AND' },
  ])
  const [natureOfService, setNatureOfService] = useState('')
  const [natureOfServiceOptions, setNatureOfServiceOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [loadingConditions, setLoadingConditions] = useState(false)
  const [fieldOptions, setFieldOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [metadataFields, setMetadataFields] = useState<TransactionalField[]>([])

  const getConditionKey = (conditionId: number, suffix: string) =>
    'condition_' + conditionId + '_' + suffix

  useEffect(() => {
    if (open) {
      if (editingRule) {
        // Incoming row dates are dd/mm/yyyy; normalise to the YYYY-MM-DD the
        // form/picker expects so edit mode does not show an empty date.
        const toIsoDate = (value?: string | null): string => {
          if (!value) return ''
          const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim())
          if (ddmmyyyy) {
            const [, dd, mm, yyyy] = ddmmyyyy
            return `${yyyy}-${mm}-${dd}`
          }
          const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
          if (iso) return value.trim()
          return ''
        }
        setRuleName(editingRule.ruleName || '')
        setStartDate(toIsoDate(editingRule.startDate))
        setEndDate(toIsoDate(editingRule.endDate))
        setActive(editingRule.status === 'Active')
        setNatureOfService(editingRule.mappedNatureOfService || '')
      } else {
        setRuleName('')
        setStartDate('')
        setEndDate('')
        setActive(true)
        setNatureOfService('')
        setConditions([
          { id: 1, field: '', operator: '', value: '', logicalOperator: 'AND' },
        ])
      }
      setErrors({})
      setSubmitting(false)
    }
  }, [open, editingRule])

  const loadServiceMasterValues = useCallback(async () => {
    if (!countryCode || !open) return
    const result = await fetchServiceMasterValues(countryCode)
    if (result.success && Array.isArray(result.data)) {
      setNatureOfServiceOptions(
        result.data.map((val: string) => ({ value: val, label: val }))
      )
    }
  }, [countryCode, open])

  useEffect(() => {
    if (open) {
      loadServiceMasterValues()
    }
  }, [open, loadServiceMasterValues])

  // --- Fetch field options from transactional data metadata API ---
  const loadFieldMetadata = useCallback(async () => {
    if (!countryCode || !open) return
    const result = await fetchTransactionalDataMetadata(countryCode)
    if (result.success && Array.isArray(result.data)) {
      setMetadataFields(result.data)
      // Only user-entered columns are valid rule-condition fields. Exclude
      // system-generated/computed columns (AI suggestions, variance, status,
      // "Reference Number", "Duplication Reference Number", "Excluded", etc.)
      // and hidden columns, and guard against malformed entries.
      const seenKeys = new Set<string>()
      setFieldOptions(
        result.data
          .filter((f: TransactionalField) => {
            if (!f || !f.field_key) return false
            if (f.system_generated === true) return false
            if (f.visible === false) return false
            if (seenKeys.has(f.field_key)) return false
            seenKeys.add(f.field_key)
            return true
          })
          .map((f: TransactionalField) => ({
            value: f.field_key,
            label: f.label ?? f.field_key,
          }))
      )
    }
  }, [countryCode, open])

  useEffect(() => {
    if (open) {
      loadFieldMetadata()
    }
  }, [open, loadFieldMetadata])

  // The conditions API returns the field as `conditionName`, which may be the
  // human LABEL (e.g. "Nature of Payment") rather than the `field_key`
  // (e.g. "nature_of_payment"). The whole form keys off `field_key` (field
  // options, metadata lookup for dropdown/date, submit payload), so a label
  // value means fieldMeta is never found and the Value box falls back to free
  // text. Resolve any stored value back to its `field_key` using metadata,
  // matching by field_key first, then by (case-insensitive) label.
  const resolveFieldKey = useCallback(
    (stored: string, fields: TransactionalField[]): string => {
      if (!stored) return stored
      const byKey = fields.find(f => f.field_key === stored)
      if (byKey) return byKey.field_key
      const norm = stored.trim().toLowerCase()
      const byLabel = fields.find(
        f => (f.label ?? '').trim().toLowerCase() === norm
      )
      return byLabel ? byLabel.field_key : stored
    },
    []
  )

  const loadRuleConditions = useCallback(async () => {
    if (!editingRule?.uuid || !open) return
    setLoadingConditions(true)
    const result = await fetchRuleConditions(editingRule.uuid)
    if (result.success && Array.isArray(result.data)) {
      const formConditions: FormCondition[] = result.data
        .sort(
          (a: RuleCondition, b: RuleCondition) =>
            ((a as any).conditionOrder ?? 0) - ((b as any).conditionOrder ?? 0)
        )
        .map((c: RuleCondition, idx: number) => ({
          id: idx + 1,
          conditionId: c.conditionId,
          // Normalise to field_key so the metadata-driven dropdown/date/
          // multi-select value inputs resolve correctly in edit mode.
          field: resolveFieldKey(c.conditionName, metadataFields),
          operator: c.operatorName,
          value: c.conditionValue,
          logicalOperator: (c.logicalOperator as 'AND' | 'OR') || 'AND',
        }))
      if (formConditions.length > 0) {
        setConditions(formConditions)
      }
    } else if (!result.success) {
      showSnackbar(result.error ?? 'Failed to load rule conditions', 'error')
    }
    setLoadingConditions(false)
  }, [editingRule, open, showSnackbar, metadataFields, resolveFieldKey])

  useEffect(() => {
    if (open && isEditMode) {
      loadRuleConditions()
    }
  }, [open, isEditMode, loadRuleConditions])

  // Metadata and conditions load in parallel, so conditions may have been set
  // before metadata arrived — leaving `field` as a label that won't match any
  // field_key. Once metadata is available, re-map any condition.field that is
  // still a label to its field_key so the dropdown/multi-select Value input
  // renders instead of the free-text fallback.
  useEffect(() => {
    if (!open || metadataFields.length === 0) return
    setConditions(prev => {
      let changed = false
      const next = prev.map(c => {
        if (!c.field) return c
        const resolved = resolveFieldKey(c.field, metadataFields)
        if (resolved !== c.field) {
          changed = true
          return { ...c, field: resolved }
        }
        return c
      })
      return changed ? next : prev
    })
  }, [open, metadataFields, resolveFieldKey])

  const addCondition = () => {
    const newId = Math.max(0, ...conditions.map(c => c.id)) + 1
    setConditions(prev => [
      ...prev,
      { id: newId, field: '', operator: '', value: '', logicalOperator: 'AND' },
    ])
  }

  const removeCondition = (conditionId: number) => {
    if (conditions.length <= 1) return
    setConditions(prev => prev.filter(c => c.id !== conditionId))
  }

  const handleConditionChange = (
    conditionId: number,
    field: string,
    value: string
  ) => {
    // When field changes, reset value and clear value errors
    if (field === 'field') {
      setConditions(prev =>
        prev.map(c =>
          c.id === conditionId
            ? { ...c, field: value, value: '', operator: '' }
            : c
        )
      )
      const valueKey = getConditionKey(conditionId, 'value')
      const operatorKey = getConditionKey(conditionId, 'operator')
      const fieldKey = getConditionKey(conditionId, 'field')
      setErrors(prev => {
        const next = { ...prev }
        delete next[valueKey]
        delete next[operatorKey]
        delete next[fieldKey]
        return next
      })
    } else {
      // When switching to a null-check operator, the value field is disabled;
      // clear any previously entered value so a stale value/error does not linger.
      const isNullOperator =
        field === 'operator' && (value === 'IS_NULL' || value === 'IS_NOT_NULL')

      setConditions(prev =>
        prev.map(c =>
          c.id === conditionId
            ? {
                ...c,
                [field]: value,
                ...(isNullOperator ? { value: '' } : {}),
              }
            : c
        )
      )
      const key = getConditionKey(conditionId, field)
      const valueKey = getConditionKey(conditionId, 'value')
      setErrors(prev => {
        const next = { ...prev }
        delete next[key]
        if (isNullOperator) delete next[valueKey]
        return next
      })
    }

    // Runtime metadata validation for value field
    if (field === 'value' && value) {
      const condition = conditions.find(c => c.id === conditionId)
      if (condition?.field) {
        const fieldMeta = metadataFields.find(
          f => f.field_key === condition.field
        )
        if (fieldMeta?.validations) {
          const v = fieldMeta.validations
          const valueKey = getConditionKey(conditionId, 'value')
          let hasError = false
          // Date format validation
          if (v.dateFormat) {
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/
            if (!dateRegex.test(value)) {
              setErrors(prev => ({
                ...prev,
                [valueKey]: 'Invalid date. Format: ' + v.dateFormat,
              }))
              hasError = true
            }
          }
          // Max length validation
          if (!hasError && v.maxLength && value.length > v.maxLength) {
            setErrors(prev => ({
              ...prev,
              [valueKey]: 'Max length is ' + v.maxLength + ' characters',
            }))
            hasError = true
          }
          // Regex validation
          if (!hasError && v.regex) {
            try {
              const re = new RegExp(v.regex)
              if (!re.test(value)) {
                setErrors(prev => ({ ...prev, [valueKey]: 'Invalid format' }))
                hasError = true
              }
            } catch {
              /* skip invalid regex */
            }
          }
          // Clear error if validation passes
          if (!hasError) {
            setErrors(prev => {
              const next = { ...prev }
              delete next[valueKey]
              return next
            })
          }
        }
      }
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!ruleName) newErrors.ruleName = 'Rule name is required'
    if (!startDate) newErrors.startDate = 'Start date is required'
    if (!natureOfService)
      newErrors.natureOfService = 'Nature of service is required'
    if (startDate && endDate) {
      const s = dayjs(startDate, 'YYYY-MM-DD')
      const e = dayjs(endDate, 'YYYY-MM-DD')
      if (e.isBefore(s)) newErrors.endDate = 'End date must be after start date'
    }
    conditions.forEach(c => {
      if (!c.field)
        newErrors[getConditionKey(c.id, 'field')] = 'Field is required'
      if (!c.operator)
        newErrors[getConditionKey(c.id, 'operator')] = 'Operator is required'
      const opUpper = String(c.operator).toUpperCase()
      // IS_NULL / IS_NOT_NULL need no value â€” skip all value validation.
      if (opUpper === 'IS_NULL' || opUpper === 'IS_NOT_NULL') {
        return
      }
      const cFieldMeta = metadataFields.find(f => f.field_key === c.field)
      const cDataType = mtFieldDataType(cFieldMeta?.type)
      // LIST operators: comma-separated list, each entry validated individually.
      if (LIST_OPERATORS.includes(opUpper)) {
        const parts = String(c.value || '')
          .split(',')
          .map(p => p.trim())
          .filter(Boolean)
        if (parts.length === 0) {
          newErrors[getConditionKey(c.id, 'value')] =
            'At least one value is required'
        } else {
          const v = cFieldMeta?.validations
          for (const part of parts) {
            if (cDataType === 'date' && !DATE_DDMMYYYY_REGEX.test(part)) {
              newErrors[getConditionKey(c.id, 'value')] =
                'Invalid date "' + part + '". Use DD/MM/YYYY'
              break
            }
            if (v?.maxLength && part.length > v.maxLength) {
              newErrors[getConditionKey(c.id, 'value')] =
                'Max length is ' + v.maxLength + ' characters'
              break
            }
            if (v?.regex) {
              try {
                if (!new RegExp(v.regex).test(part)) {
                  newErrors[getConditionKey(c.id, 'value')] =
                    'Invalid value "' + part + '"'
                  break
                }
              } catch {
                /* skip invalid regex */
              }
            }
          }
        }
        return
      }
      // BETWEEN: exactly two colon-separated values.
      if (opUpper === 'BETWEEN') {
        const raw = String(c.value || '').trim()
        const parts = raw.split(':').map(p => p.trim())
        if (parts.length !== 2 || !parts[0] || !parts[1]) {
          newErrors[getConditionKey(c.id, 'value')] =
            'Enter exactly two values separated by a colon (:)'
        } else if (cDataType === 'date') {
          if (
            !DATE_DDMMYYYY_REGEX.test(parts[0]) ||
            !DATE_DDMMYYYY_REGEX.test(parts[1])
          ) {
            newErrors[getConditionKey(c.id, 'value')] =
              'Both dates must be in DD/MM/YYYY format'
          }
        } else if (cDataType === 'number') {
          const numRegex = /^[-+]?\d+(\.\d+)?$/
          if (!numRegex.test(parts[0]) || !numRegex.test(parts[1])) {
            newErrors[getConditionKey(c.id, 'value')] =
              'Both values must be valid numbers'
          } else if (parseFloat(parts[0]) >= parseFloat(parts[1])) {
            newErrors[getConditionKey(c.id, 'value')] =
              'First value must be less than the second value'
          }
        }
        return
      }
      if (!c.value) {
        newErrors[getConditionKey(c.id, 'value')] = 'Value is required'
      }
      // Validate value against metadata field validations
      if (c.value && c.field && metadataFields.length > 0) {
        const fieldMeta = metadataFields.find(f => f.field_key === c.field)
        if (fieldMeta?.validations) {
          const v = fieldMeta.validations
          // Date format validation
          if (v.dateFormat) {
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/
            if (!dateRegex.test(c.value)) {
              newErrors[getConditionKey(c.id, 'value')] =
                'Invalid date. Format: ' + v.dateFormat
            }
          }
          // Max length validation
          else if (v.maxLength && c.value.length > v.maxLength) {
            newErrors[getConditionKey(c.id, 'value')] =
              'Max length is ' + v.maxLength + ' characters'
          }
          // Regex validation
          if (v.regex && !newErrors[getConditionKey(c.id, 'value')]) {
            try {
              const re = new RegExp(v.regex)
              if (!re.test(c.value)) {
                newErrors[getConditionKey(c.id, 'value')] = 'Invalid format'
              }
            } catch {
              /* skip invalid regex */
            }
          }
        }
      }
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    if (!countryCode || !apiIdentifierUuid) {
      showSnackbar('Please select an entity and TAN first', 'error')
      return
    }
    setSubmitting(true)
    const identifierFields = tanApplicable
      ? { tanMasterUuid: apiIdentifierUuid, entityMasterUuid: null }
      : { entityMasterUuid: apiIdentifierUuid, tanMasterUuid: null }
    const conditionsPayload: RuleCondition[] = conditions.map(c => ({
      conditionId: c.conditionId,
      conditionName: c.field,
      operatorName: c.operator,
      conditionValue: c.value,
      logicalOperator: c.logicalOperator,
    }))

    if (isEditMode && editingRule) {
      const payload: RuleUpdateRequest = {
        ruleId: editingRule.uuid,
        ...identifierFields,
        startDate,
        endDate,
        active,
        conditions: conditionsPayload,
      }
      const result = await updateRule(countryCode, payload)
      if (result.success) {
        showSnackbar('Rule updated successfully', 'success')
        onSuccess()
      } else {
        showSnackbar(result.error ?? 'Failed to update rule', 'error')
      }
    } else {
      const payload: RuleCreateRequest = {
        ruleName,
        ...identifierFields,
        mappedServiceValue: natureOfService,
        startDate,
        endDate,
        active,
        conditions: conditionsPayload,
      }
      const result = await createRule(countryCode, payload)
      if (result.success) {
        showSnackbar('Rule created successfully', 'success')
        onSuccess()
      } else {
        showSnackbar(result.error ?? 'Failed to create rule', 'error')
      }
    }
    setSubmitting(false)
  }
  return (
    <Dialog open={open} onClose={onClose} fullScreen maxWidth="lg">
      <Layout>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            py: '20px',
            px: '32px',
            backgroundColor: 'var(--bg-white, #FFFFFF)',
            boxShadow: '0px 2px 4px -2px rgba(0, 0, 0, 0.12)',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'charter',
              fontWeight: 600,
              fontSize: 'var(--font-size-xl, 20px)',
              lineHeight: '130%',
              color: 'var(--text-heading, #252525)',
            }}
          >
            {isEditMode ? 'Edit rule' : 'Add new rule'}
          </Typography>
        </Box>

        <DialogContent sx={{ p: 3, pb: 12 }}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="en-gb"
          >
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 3,
                  mb: 3,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%',
                    }}
                  >
                    Rule Name
                    <span style={{ color: 'var(--forbrand-error)' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    value={ruleName}
                    onChange={e => {
                      setRuleName(e.target.value)
                      if (errors.ruleName)
                        setErrors(prev => {
                          const n = { ...prev }
                          delete n.ruleName
                          return n
                        })
                    }}
                    placeholder="Enter rule name"
                    error={!!errors.ruleName}
                    helperText={errors.ruleName}
                    disabled={isEditMode}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isEditMode
                          ? 'var(--bg-disabled)'
                          : '#fff',
                        fontSize: '14px',
                        height: '48px',
                        borderRadius: 'unset',
                        '& fieldset': {
                          borderColor: errors.ruleName
                            ? 'var(--forbrand-error)'
                            : '#d0d0d0',
                        },
                        '&:hover fieldset': {
                          borderColor: errors.ruleName
                            ? 'var(--forbrand-error)'
                            : '#a0a0a0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: errors.ruleName
                            ? 'var(--forbrand-error)'
                            : 'var(--forbrand-color)',
                          borderWidth: '1px',
                        },
                      },
                    }}
                    slotProps={{
                      formHelperText: { sx: { margin: 0, mt: 0.5 } },
                      htmlInput: { maxLength: 100 },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%',
                    }}
                  >
                    Start Date
                    <span style={{ color: 'var(--forbrand-color)' }}>*</span>
                  </Typography>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={startDate ? dayjs(startDate, 'YYYY-MM-DD') : null}
                    onChange={newValue => {
                      if (newValue) {
                        setStartDate(newValue.format('YYYY-MM-DD'))
                        if (errors.startDate)
                          setErrors(prev => {
                            const n = { ...prev }
                            delete n.startDate
                            return n
                          })
                      }
                    }}
                    slotProps={
                      {
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: !!errors.startDate,
                          helperText: errors.startDate,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 'unset',
                              backgroundColor: 'white',
                              height: '48px',
                              '& fieldset': {
                                borderColor: errors.startDate
                                  ? 'var(--forbrand-error)'
                                  : '#d0d0d0',
                              },
                              '&:hover fieldset': {
                                borderColor: errors.startDate
                                  ? 'var(--forbrand-error)'
                                  : '#a0a0a0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: errors.startDate
                                  ? 'var(--forbrand-error)'
                                  : 'var(--forbrand-color)',
                                borderWidth: '1px',
                              },
                            },
                            '& .MuiFormHelperText-root': { margin: 0, mt: 0.5 },
                          },
                        },
                      } as any
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%',
                    }}
                  >
                    End Date
                  </Typography>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={endDate ? dayjs(endDate, 'YYYY-MM-DD') : null}
                    onChange={newValue => {
                      if (newValue) setEndDate(newValue.format('YYYY-MM-DD'))
                    }}
                    slotProps={
                      {
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: !!errors.endDate,
                          helperText: errors.endDate,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 'unset',
                              backgroundColor: 'white',
                              height: '48px',
                              '& fieldset': {
                                borderColor: errors.endDate
                                  ? 'var(--forbrand-error)'
                                  : '#d0d0d0',
                              },
                              '&:hover fieldset': {
                                borderColor: errors.endDate
                                  ? 'var(--forbrand-error)'
                                  : '#a0a0a0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: errors.endDate
                                  ? 'var(--forbrand-error)'
                                  : 'var(--forbrand-color)',
                                borderWidth: '1px',
                              },
                            },
                            '& .MuiFormHelperText-root': { margin: 0, mt: 0.5 },
                          },
                        },
                      } as any
                    }
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}
                >
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 500, color: '#333' }}
                  >
                    Active
                  </Typography>
                  <StyledSwitch
                    checked={active}
                    onChange={(e: any) => setActive(e.target.checked)}
                  />
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>

          <Paper
            sx={{ p: { xs: 2, sm: 2 }, mb: 2, backgroundColor: '#FAFAFA' }}
            elevation={0}
          >
            {loadingConditions ? (
              <Typography sx={{ p: 2, color: '#666' }}>
                Loading conditions...
              </Typography>
            ) : (
              <Box>
                {conditions.map((condition, index) => {
                  const fieldKey = getConditionKey(condition.id, 'field')
                  const operatorKey = getConditionKey(condition.id, 'operator')
                  const valueKey = getConditionKey(condition.id, 'value')
                  // Data-type aware operator list + value input (parity with
                  // the India AddRuleSetting form).
                  const fieldMeta = metadataFields.find(
                    f => f.field_key === condition.field
                  )
                  const dataType = mtFieldDataType(fieldMeta?.type)
                  const isNumericOrDate =
                    dataType === 'number' || dataType === 'date'
                  const operatorList = isNumericOrDate
                    ? OPERATOR_OPTIONS
                    : OPERATORS_WITHOUT_NUMERIC
                  const op = String(condition.operator).toUpperCase()
                  const isListOperator = LIST_OPERATORS.includes(op)
                  const isBetween = op === 'BETWEEN'
                  const isNullOp = op === 'IS_NULL' || op === 'IS_NOT_NULL'
                  // If the selected field is a dropdown in the metadata, offer
                  // its options as a Select for single-value operators (EQUAL_TO
                  // etc.). List/Between operators keep the free-text box because
                  // they need multiple / range values.
                  const fieldOptionsForValue = Array.isArray(
                    (fieldMeta as any)?.options
                  )
                    ? ((fieldMeta as any).options as {
                        label: string
                        value: string
                      }[])
                    : []
                  const isDropdownField =
                    (fieldMeta?.type || '').toLowerCase() === 'dropdown' &&
                    fieldOptionsForValue.length > 0
                  // When the selected field is a metadata dropdown, render its
                  // options as a single-select CustomSelect for the Value
                  // (including list operators — one value is picked from the
                  // configured options). Between keeps the free-text range box
                  // and null operators have no value input.
                  const useValueDropdown =
                    isDropdownField && !isBetween && !isNullOp
                  // For single-value operators on a date field, show a date
                  // picker instead of a free-text box. List/Between operators
                  // keep the free-text box (comma separated / range values).
                  const useValueDatePicker =
                    dataType === 'date' &&
                    !isListOperator &&
                    !isBetween &&
                    !isNullOp
                  const valuePlaceholder = isListOperator
                    ? dataType === 'date'
                      ? 'Example: 10/12/2019,21/12/2019'
                      : 'Enter comma separated values (e.g. A,B,C)'
                    : isBetween
                      ? dataType === 'date'
                        ? 'Example: 10/12/2019:21/12/2019'
                        : 'Example: 100:500'
                      : 'Enter value'
                  return (
                    <Box key={condition.id} sx={{ mb: 3 }}>
                      {index > 0 && (
                        <Box sx={{ my: 2 }}>
                          <CustomSelect
                            label=""
                            placeholder=""
                            options={LOGICAL_OPERATOR_OPTIONS}
                            getOptionLabel={(opt: any) => opt.label}
                            getOptionValue={(opt: any) => opt.value}
                            value={condition.logicalOperator}
                            onChange={(e: any) =>
                              handleConditionChange(
                                condition.id,
                                'logicalOperator',
                                e.target.value
                              )
                            }
                            sx={{ width: 120 }}
                          />
                        </Box>
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          color: '#333',
                          mb: 2,
                        }}
                      >
                        Condition {index + 1}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          gap: 2,
                          alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        }}
                      >
                        <CustomSelect
                          label="Field"
                          placeholder="Select Field"
                          options={fieldOptions}
                          getOptionLabel={(opt: any) => opt.label}
                          getOptionValue={(opt: any) => opt.value}
                          value={condition.field || ''}
                          onChange={(e: any) =>
                            handleConditionChange(
                              condition.id,
                              'field',
                              e.target.value
                            )
                          }
                          error={!!errors[fieldKey]}
                          helperText={errors[fieldKey]}
                          fullWidth
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                maxWidth: 200,
                                '& .MuiMenuItem-root': {
                                  whiteSpace: 'normal',
                                  wordWrap: 'break-word',
                                },
                              },
                            },
                          }}
                        />
                        <CustomSelect
                          label="Operator"
                          placeholder="Select Operator"
                          options={operatorList}
                          getOptionLabel={(opt: any) => opt.label}
                          getOptionValue={(opt: any) => opt.value}
                          value={condition.operator || ''}
                          onChange={(e: any) =>
                            handleConditionChange(
                              condition.id,
                              'operator',
                              e.target.value
                            )
                          }
                          error={!!errors[operatorKey]}
                          helperText={errors[operatorKey]}
                          fullWidth
                        />
                        <Box sx={{ flex: 1, minWidth: 225, width: '100%' }}>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 500,
                              color: 'var(--forbrand-primary)',
                              mb: 1,
                              lineHeight: '140%',
                            }}
                          >
                            Value
                          </Typography>
                          {useValueDropdown ? (
                            <CustomSelect
                              label=""
                              placeholder="Select value"
                              options={fieldOptionsForValue}
                              getOptionLabel={(opt: any) => opt.label}
                              getOptionValue={(opt: any) => opt.value}
                              value={condition.value || ''}
                              onChange={(e: any) =>
                                handleConditionChange(
                                  condition.id,
                                  'value',
                                  e.target.value
                                )
                              }
                              disabled={isNullOp}
                              error={!!errors[valueKey]}
                              helperText={errors[valueKey]}
                              fullWidth
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: 300,
                                    maxWidth: 320,
                                    '& .MuiMenuItem-root': {
                                      whiteSpace: 'normal',
                                      wordWrap: 'break-word',
                                    },
                                  },
                                },
                              }}
                            />
                          ) : useValueDatePicker ? (
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale="en-gb"
                            >
                              <DatePicker
                                format="DD/MM/YYYY"
                                value={
                                  condition.value
                                    ? dayjs(condition.value, 'DD/MM/YYYY')
                                    : null
                                }
                                onChange={(newValue: any) =>
                                  handleConditionChange(
                                    condition.id,
                                    'value',
                                    newValue
                                      ? newValue.format('DD/MM/YYYY')
                                      : ''
                                  )
                                }
                                disabled={isNullOp}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    size: 'medium',
                                    error: !!errors[valueKey],
                                    helperText: errors[valueKey],
                                    sx: {
                                      '& .MuiOutlinedInput-root': {
                                        height: '48px',
                                        fontSize: '14px',
                                        borderRadius: 'unset',
                                        backgroundColor: isNullOp
                                          ? 'var(--bg-disabled)'
                                          : '#fff',
                                        '& fieldset': {
                                          borderColor: errors[valueKey]
                                            ? 'var(--forbrand-error)'
                                            : '#d0d0d0',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: errors[valueKey]
                                            ? 'var(--forbrand-error)'
                                            : '#a0a0a0',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: errors[valueKey]
                                            ? 'var(--forbrand-error)'
                                            : 'var(--forbrand-color)',
                                          borderWidth: '1px',
                                        },
                                      },
                                      '& .MuiFormHelperText-root': {
                                        margin: 0,
                                        mt: 0.5,
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          ) : (
                            <TextField
                              fullWidth
                              size="medium"
                              value={condition.value}
                              onChange={e =>
                                handleConditionChange(
                                  condition.id,
                                  'value',
                                  e.target.value
                                )
                              }
                              placeholder={valuePlaceholder}
                              disabled={isNullOp}
                              error={!!errors[valueKey]}
                              helperText={errors[valueKey]}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: '48px',
                                  fontSize: '14px',
                                  borderRadius: 'unset',
                                  backgroundColor: isNullOp
                                    ? 'var(--bg-disabled)'
                                    : '#fff',
                                  '& fieldset': {
                                    borderColor: errors[valueKey]
                                      ? 'var(--forbrand-error)'
                                      : '#d0d0d0',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: errors[valueKey]
                                      ? 'var(--forbrand-error)'
                                      : '#a0a0a0',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: errors[valueKey]
                                      ? 'var(--forbrand-error)'
                                      : 'var(--forbrand-color)',
                                    borderWidth: '1px',
                                  },
                                },
                              }}
                              slotProps={{
                                formHelperText: { sx: { margin: 0, mt: 0.5 } },
                              }}
                            />
                          )}
                        </Box>
                        {conditions.length > 1 && (
                          <IconButton
                            onClick={() => removeCondition(condition.id)}
                            sx={{
                              color: 'var(--forbrand-error)',
                              border: '1px solid #EF4444',
                              borderRadius: '4px',
                              mt: { xs: 0, md: '32px' },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Paper>

          <Button
            startIcon={<AddIcon />}
            onClick={addCondition}
            sx={{
              color: '#EF4444',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              mb: 1,
            }}
          >
            Add Condition
          </Button>

          <Box sx={{ mb: 6 }}>
            <CustomSelect
              label="Nature of Service"
              placeholder="Select nature of service"
              options={natureOfServiceOptions}
              getOptionLabel={(opt: any) => opt.label}
              getOptionValue={(opt: any) => opt.value}
              value={natureOfService || ''}
              onChange={(e: any) => {
                setNatureOfService(e.target.value)
                if (errors.natureOfService)
                  setErrors(prev => {
                    const n = { ...prev }
                    delete n.natureOfService
                    return n
                  })
              }}
              error={!!errors.natureOfService}
              helperText={errors.natureOfService}
              required
              disabled={isEditMode}
              sx={{
                width: { xs: '100%', md: '40%' },
                height: '48px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: errors.tdsSection
                    ? 'var(--forbrand-error)'
                    : 'var(--forbrand-color)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    maxWidth: 200,
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    },
                  },
                },
              }}
            />
          </Box>
        </DialogContent>

        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: { xs: 0, md: isCollapsed ? '65px' : '300px' },
            right: 0,
            backgroundColor: '#fff',
            borderTop: '1px solid var(--border-subtel)',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: 1.5,
            zIndex: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: 'var(--forbrand-primary)',
              borderColor: 'var(--border-subtel)',
              borderRadius: '4px',
              minWidth: '80px',
              height: '36px',
            }}
          >
            Cancel
          </Button>
          <CustomButton
            onClick={handleSubmit}
            size="small"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save'}
          </CustomButton>
        </Box>
      </Layout>
    </Dialog>
  )
}
